import { combine, withCallbacks, withContext } from 'react-hooks-combine'
import { ServicesContext } from 'services/context'
import { StoreContext } from 'store/context'
import { toaster } from 'utils/toast-wrapper'
import { withFormModel } from 'shared/hooks'
import { spec, validators } from 'utils/validation'

import MobileInWinModalComponent from './component'

const { any, string } = validators

const listTriggers = (triggers) => {
	return triggers.map((item) => ({
		id: item.id,
		name: item.description,
		description: item.description,
		...item
	}))
}

const listBeneficiaries = (beneficiaries) => {
	return beneficiaries.map((item) => ({
		id: item.id,
		beneficiaryName: item.beneficiaryName,
		...item
	}))
}

const listSponsors = (sponsors) => {
	return sponsors.map((item) => ({
		id: item.id,
		fullName: item.fullName,
		limit: item.limit,
		...item
	}))
}

export const MobileInWinModal = combine({
	hooks: [
		withContext('appStore', StoreContext),
		withContext(
			'memberService',
			ServicesContext,
			({ memberService }) => memberService
		),
		withContext(
			'followerService',
			ServicesContext,
			({ followerService }) => followerService
		),
		withContext(
			'triggerService',
			ServicesContext,
			({ triggerService }) => triggerService
		),
		withContext(
			'sponsorsService',
			ServicesContext,
			({ sponsorsService }) => sponsorsService
		),

		withFormModel(
			() => ({
				beneficiary: {
					initialValue: '',
					spec: spec.flow(any.required),
				},
				triggers: {
					initialValue: '',
					spec: spec.flow(any.required),
				},
				amount: {
					initialValue: '',
					spec: spec.flow(string.isNotEmpty, any.required),
				},
				sponsors: {
					initialValue: null,
					spec: spec.flow(),
				},
				sponsorAmount: {
					initialValue: 0,
					spec: spec.flow(),
				},
			}),
			{ id: 'mobile-inwin-form' }
		),

		withCallbacks(
			{
				loadBeneficiaries:
					({ memberService }) =>
						async (inputValue) => {
							try {
								const response = await memberService.queryBeneficiaries({
									beneficiaryName: inputValue,
									size: 20,
								})
								return listBeneficiaries(response.content || [])
							} catch (error) {
								return []
							}
						},

				loadTriggers:
					({ triggerService }) =>
						async (inputValue, { game }) => {
							try {
								if (!game?.id) {
									return []
								}

								const response = await triggerService.queryEventTriggers(game.id)
								return listTriggers(response.content || [])
							} catch (error) {
								return []
							}
						},

				loadSponsors:
					({ sponsorsService }) =>
						async (inputValue) => {
							try {
								const response = await sponsorsService.getListOfSponsors({
									fullName: inputValue,
									size: 20,
								})
								return listSponsors(response.content || [])
							} catch (error) {
								return []
							}
						},

				onSubmit:
					(
						{ memberService, followerService, appStore, form },
						{ onClose, game }
					) =>
						async (formData) => {
							try {
								const { beneficiary, triggers, amount, sponsors, sponsorAmount } = form.fields

								if (!beneficiary.value || !triggers.value || !amount.value) {
									toaster.error('Please fill in all required fields')
									return
								}

								const inWinFormDTO = {
									beneficiary: beneficiary.value,
									triggers: triggers.value,
									amount: Number.parseFloat(amount.value),
									sponsors: sponsors.value,
									sponsorAmount: Number.parseFloat(sponsorAmount.value || 0),
								}

								// Call the binary InWin API
								await memberService.binaryInWin(inWinFormDTO)

								// toaster.success('InWin has been successfully created')

								// Update user balance
								const entity = appStore.auth.loggedEntity
								if (entity?.wallet) {
									await entity.wallet.requestBalance()
								}

								// Update InWins count
								await appStore.inwins.load()

								// Don't auto-close modal - let component handle success flow
								// if (onClose) {
								// 	onClose()
								// }

							} catch (error) {
								// Re-throw error so component can handle it with custom popup
								throw error
							}
						},
			},
			[]
		),
	],

	transformProps: ({
		appStore,
		form,
		loadBeneficiaries,
		loadTriggers,
		loadSponsors,
		onSubmit,
		game,
		...props
	}) => {
		const loggedEntity = appStore.auth.loggedEntity
		const currentBalance = loggedEntity?.wallet?.balance || 0

		// Create trigger loader with game context
		const triggerLoader = (inputValue) => loadTriggers(inputValue, { game })

		return {
			...props,
			game,
			form,
			onSubmit,
			currentBalance,
			loadBeneficiaries,
			loadTriggers: triggerLoader,
			loadSponsors,
		}
	},
})(MobileInWinModalComponent) 
